import {
  I2cRadioButton,
  I2cCheckbox,
  I2cCheckboxGroup,
  I2cRadioGroup,
  I2cListItem,
  I2cInput,
} from "@webcomponents/i2cwebcomponents/dist/react";
import "../../PrepaidComponents/Prepaid.scss";
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import ErrorMessage from "../../../Components/SharedComponent/ErrorMessage/ErrorMessage";
import {
  agentWebsiteLang,
  hostVirtualCardOptions,
  technologySpecforMobileSDKOptions,
} from "./stringUtils";
import {
  handleCheckboxArrayToggle,
  handleInputChange,
  handleRadioChange,
} from "./formUtils";
import { validationSchemaSDK } from "./ValidationSchema";

const SDK = ({
  setAddFormData,
  addFormData,
  setFormValidStates,
  formDataJson,
  isFetching,
}) => {
  // Initialize React Hook Form with Yup resolver
  const methods = useForm({
    resolver: yupResolver(validationSchemaSDK),
    mode: "all",
    reValidateMode: "all",
    shouldUseNativeValidation: false,
    criteriaMode: "all",
    defaultValues: {
      sdk_would_you_like_i2c_hosted_mobile_sdk_application: "no",
      sdk_technology_specification_for_i2c_mobile_sdk_integration: [],
      sdk_technology_specification_for_i2c_mobile_sdk_integration_other: "",
      sdk_i2c_hosted_mobile_sdk_language: [],
      sdk_i2c_hosted_mobile_sdk_language_other: "",
    },
  });

  const {
    formState: { errors },
  } = methods;

  const fields = [
    "sdk_would_you_like_i2c_hosted_mobile_sdk_application",
    "sdk_technology_specification_for_i2c_mobile_sdk_integration",
    "sdk_technology_specification_for_i2c_mobile_sdk_integration_other",
    "sdk_i2c_hosted_mobile_sdk_language",
    "sdk_i2c_hosted_mobile_sdk_language_other",
  ];

  // Handle value changes and update parent state
  const handleValueChanges = React.useCallback(() => {
    const values = methods.getValues();
    setAddFormData({
      ...addFormData,
      3: {
        ...addFormData["3"],
        "3_9": { ...values },
      },
    });
  }, [methods, setAddFormData, addFormData]);

  // Load existing form data
  useEffect(() => {
    if (!isFetching && Object.keys(formDataJson ?? {}).length) {
      fields.forEach((field) => {
        const value = formDataJson[3]?.["3_9"]?.[field];
        if (value !== undefined && value !== null) {
          methods.setValue(field, value);
        }
      });
      methods.trigger();
      handleValueChanges();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetching]);

  // Trigger validation when component mounts
  useEffect(() => {
    methods.trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update form validation state when errors change
  useEffect(() => {
    const errorsLength = Object.keys(errors)?.length;
    if (errorsLength === 0) {
      setFormValidStates("3_9", true);
    } else {
      setFormValidStates("3_9", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  const isHostVirtualCardRequired =
    methods.watch("sdk_would_you_like_i2c_hosted_mobile_sdk_application") === "yes";

  return (
    <React.Fragment>
      <div className="forms-instance">
        <div i2c-grid="grid gap-md">
          <div i2c-grid="12">
            <div className="section-space">
              <div className="sub-heading mb-16">
                Would you like i2c hosted “Mobile SDK Application”?
              </div>
              <I2cRadioGroup
                label=""
                value={methods.watch("sdk_would_you_like_i2c_hosted_mobile_sdk_application")}
                name="sdk_would_you_like_i2c_hosted_mobile_sdk_application"
                onI2cChange={(e) => {
                  const value = e.target?.value || e;

                  handleRadioChange(
                    methods,
                    "sdk_would_you_like_i2c_hosted_mobile_sdk_application",
                    value,
                    value !== "yes" ? {
                      sdk_technology_specification_for_i2c_mobile_sdk_integration: [],
                      sdk_technology_specification_for_i2c_mobile_sdk_integration_other: "",
                          sdk_i2c_hosted_mobile_sdk_language: [],
                          sdk_i2c_hosted_mobile_sdk_language_other: "",
                    } : {},
                    handleValueChanges
                  );
                }}
              >
                {hostVirtualCardOptions.map((option) => (
                  <I2cRadioButton key={option.value} value={option.value}>
                    {option.label}
                  </I2cRadioButton>
                ))}
              </I2cRadioGroup>
              {errors.sdk_would_you_like_i2c_hosted_mobile_sdk_application && (
                <ErrorMessage  show={true}  message={errors.sdk_would_you_like_i2c_hosted_mobile_sdk_application.message} />
              )}
            </div>
            {isHostVirtualCardRequired && (
              <div className="section-space">
                <div className="sub-heading mb-16">
                  Technology Specification for i2c mobile SDK Integration
                </div>
                <I2cCheckboxGroup
                  key={`virtual-card-technology-${methods.watch("sdk_would_you_like_i2c_hosted_mobile_sdk_application")}`}
                  className="mb-16"
                >
                  <div i2c-grid="grid gap-sm">
                    {technologySpecforMobileSDKOptions.map((tech, index) => (
                      <I2cListItem
                        key={index}
                        i2c-grid="4@md"
                        variant="receipt"
                        label={tech.label}
                        value=""
                        type="checkbox"
                        checked={methods.watch("sdk_technology_specification_for_i2c_mobile_sdk_integration").includes(tech.value)}
                        onI2cCheckboxClicked={() =>
                          handleCheckboxArrayToggle(
                            methods,
                            "sdk_technology_specification_for_i2c_mobile_sdk_integration",
                            tech.value,
                            { dependentField: "sdk_technology_specification_for_i2c_mobile_sdk_integration_other" },
                            handleValueChanges
                          )
                        }
                      >
                        <I2cCheckbox
                          slot="prefix"
                          name="sdk_technology_specification_for_i2c_mobile_sdk_integration"
                          value={tech.value}
                          label=""
                          filled
                          checked={methods.watch("sdk_technology_specification_for_i2c_mobile_sdk_integration").includes(tech.value)}
                          className="customCheckbox"
                        ></I2cCheckbox>
                      </I2cListItem>
                    ))}
                  </div>
                </I2cCheckboxGroup>
                {errors.sdk_technology_specification_for_i2c_mobile_sdk_integration && (
                  <ErrorMessage  show={true}  message={errors.sdk_technology_specification_for_i2c_mobile_sdk_integration.message} />
                )}
                {methods
                  .watch(
                    "sdk_technology_specification_for_i2c_mobile_sdk_integration",
                  )
                  .includes("other") && (
                  <div i2c-grid="grid gap-sm" className="section-space">
                    <div i2c-grid="4@md">
                      <div className="single-field-wrapper">
                        <I2cInput
                          className="single-input-field"
                          placeholder="Please Specify"
                          size="large"
                          label="Enter Details"
                          name="sdk_technology_specification_for_i2c_mobile_sdk_integration_other"
                          value={methods.watch(
                            "sdk_technology_specification_for_i2c_mobile_sdk_integration_other",
                          )}
                          onI2cInput={(e) =>
                            handleInputChange(
                              methods,
                              "sdk_technology_specification_for_i2c_mobile_sdk_integration_other",
                              e.target.value,
                              handleValueChanges,
                            )
                          }
                          invalid={
                            errors.sdk_technology_specification_for_i2c_mobile_sdk_integration_other
                              ? true
                              : false
                          }
                        />
                        {errors.sdk_technology_specification_for_i2c_mobile_sdk_integration_other && (
                          <ErrorMessage
                            show={true}
                            message={
                              errors
                                .sdk_technology_specification_for_i2c_mobile_sdk_integration_other
                                .message
                            }
                          />
                        )}
                      </div>
                    </div>
                  </div>
                )}
     
              </div>
              
            )}
            <div className="section-space">
                  <div className="sub-heading mb-16">
                    i2c hosted Mobile SDK Language
                  </div>
                  <I2cCheckboxGroup
                    key={`virtual-card-language-${methods.watch("sdk_would_you_like_i2c_hosted_mobile_sdk_application")}`}
                  >
                    <div i2c-grid="grid gap-sm">
                      {agentWebsiteLang.map((web, index) => (
                        <I2cListItem
                          key={index}
                          i2c-grid="4@md"
                          variant="receipt"
                          label={web.label}
                          value=""
                          type="checkbox"
                          checked={methods.watch("sdk_i2c_hosted_mobile_sdk_language").includes(web.value)}
                          onI2cCheckboxClicked={() =>
                            handleCheckboxArrayToggle(
                              methods,
                              "sdk_i2c_hosted_mobile_sdk_language",
                              web.value,
                              { dependentField: "sdk_i2c_hosted_mobile_sdk_language_other" },
                              handleValueChanges
                            )
                          }
                        >
                          <I2cCheckbox
                            slot="prefix"
                            name="sdk_i2c_hosted_mobile_sdk_language"
                            value={web.value}
                            label=""
                            filled
                            checked={methods.watch("sdk_i2c_hosted_mobile_sdk_language").includes(web.value)}
                            className="customCheckbox"
                          ></I2cCheckbox>
                        </I2cListItem>
                      ))}
                    </div>
                  </I2cCheckboxGroup>
                  {errors.sdk_i2c_hosted_mobile_sdk_language && (
                    <ErrorMessage  show={true}  message={errors.sdk_i2c_hosted_mobile_sdk_language.message} />
                  )}
                  {methods
                    .watch("sdk_i2c_hosted_mobile_sdk_language")
                    .includes("other") && (
                    <div i2c-grid="grid gap-sm" className="mt-16">
                      <div i2c-grid="4@md">
                        <div className="single-field-wrapper">
                          <I2cInput
                            className="single-input-field"
                            placeholder="Please Specify"
                            size="large"
                            label="Enter Details"
                            name="sdk_i2c_hosted_mobile_sdk_language_other"
                            value={methods.watch(
                              "sdk_i2c_hosted_mobile_sdk_language_other",
                            )}
                            onI2cInput={(e) =>
                              handleInputChange(
                                methods,
                                "sdk_i2c_hosted_mobile_sdk_language_other",
                                e.target.value,
                                handleValueChanges,
                              )
                            }
                            invalid={
                              errors.sdk_i2c_hosted_mobile_sdk_language_other
                                ? true
                                : false
                            }
                          />
                          {errors.sdk_i2c_hosted_mobile_sdk_language_other && (
                            <ErrorMessage
                              show={true}
                              message={
                                errors.sdk_i2c_hosted_mobile_sdk_language_other
                                  .message
                              }
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default SDK;
